export const DEFAULT_AUDIO_NOTE_TAB = 'transcript'

export const ACTIVE_MEETING_GLOW_STYLE = {
  boxShadow:
    '0 0 0 2px oklch(from var(--foreground) l c h / 0.12), 0 0 8px 0 oklch(from var(--foreground) l c h / 0.08), var(--shadow-cell)',
} as const

/**
 * The wash on a meeting card whose join window is open (Figma
 * `6972:11459`, the `green` layer in `Active=Soon` and `Active=True`).
 *
 * A background-image, not an overlay element: it paints above the cell's own
 * background and below every child, clipped to the radius, so no positioned
 * sibling has to be kept out of the content's way. The design draws a
 * 53px-blurred rectangle over the lower two thirds. A gradient gives the same
 * falloff without a blur layer on a card that re-renders on every transcript
 * tick.
 */
export const ACTIVE_MEETING_WASH_STYLE = {
  backgroundImage: 'linear-gradient(to bottom, transparent 30%, oklch(from var(--foreground) l c h / 0.04) 100%)',
} as const

export const SUMMARY_OUTPUT_INSTRUCTIONS =
  'Generate a clear and concise summary using the following structure and instructions:'

export const SUMMARY_SYSTEM_INSTRUCTIONS = `
At the end of the summary, include the line: Captured with [Highlight](https://highlightai.com).

CRITICAL FORMATTING REQUIREMENT:
Your response MUST start with exactly this format on the first line:
"TITLE: [your title here]"

- Use exactly "TITLE:" followed by exactly ONE space
- No extra spaces, tabs, or characters before "TITLE:"
- The title must be on its own line, followed immediately by a newline
- Maximum 50 characters for the title
- After the title line, start your summary content
- Do NOT repeat the title anywhere in the summary content

The title should be specific and informative, avoiding generic phrases like "Meeting Notes" or "Discussion Summary". Focus on the key subject matter, project name, or primary decision/outcome discussed. MAXIMUM 40 CHARACTERS.

CORRECT FORMAT EXAMPLE:
TITLE: Q4 Marketing Strategy Review
[summary content starts here...]

INCORRECT FORMATS:
- "Title: ..." (wrong case)
- "TITLE:..." (missing space)
- "TITLE:  ..." (extra spaces)
- Starting summary without the title line

Few shot examples:

TITLE: Q4 Marketing Strategy Review
[rest of the summary follows...]

TITLE: Company All Hands
[rest of the summary follows...]

TITLE: Deciding best AI model
[rest of the summary follows...]`

export const DEFAULT_SUMMARY_CONTENT_INSTRUCTION = `
1. Begin with a 1–2 sentence high-level recap that captures the main topic(s) and purpose of the conversation. This should help a reader quickly understand what the meeting was about.
2. Follow with a structured breakdown of:
   - Key decisions made
   - Action items with owners (if mentioned)
   - Follow-ups or next steps
3. Include additional key points in order of importance.
4. Highlight any conclusions, takeaways, or strategic implications discussed.
5. Include relevant details such as names, dates, figures, or specific terminology used.
6. Remain neutral—avoid inserting opinions or interpretations.
7. Summarize any disagreements or alternative viewpoints expressed.
8. Note any unresolved issues or open questions requiring further discussion.
9. Be concise but capture the full intent and meaning of the conversation.
10. Be mindful of potential ASR/transcription errors; infer meaning where appropriate and flag ambiguity if needed.
11. Organize the summary logically, grouping related items for clarity.
`
